import { useEffect, useRef } from 'react';
import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interaction from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import { Card, CardBody, CardFooter } from 'reactstrap';
import { VisitCalendarEvent } from '../adapters/calendarEventsAdapter';

interface Props {
  events: VisitCalendarEvent[];
  selectedVisitId?: string | null;
  // Clic en una visita: abre su detalle.
  onOpenDetail: (visitId: string) => void;
  footer?: React.ReactNode;
}

// Cada persona tiene un punto de color fijo (según su nombre); la píldora es
// neutra para que no choque con el color de estado de la visita.
const DOT_COLORS = [
  '#00b66b',
  '#0b7bb0',
  '#7c5cd6',
  '#e0568a',
  '#c77700',
  '#0e8a8a',
  '#5b6f68',
];
const colorFor = (name: string) => {
  let hash = 0;
  for (const char of name) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return DOT_COLORS[hash % DOT_COLORS.length];
};
const isMissing = (name?: string) => !name || name.trim().toUpperCase() === 'ND';

// Calendario de sólo lectura del dashboard (PreVenta, Renta y Secondary).
const DashboardVisitsCalendar = ({
  events,
  selectedVisitId,
  onOpenDetail,
  footer,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<Calendar | null>(null);
  const onOpenDetailRef = useRef(onOpenDetail);
  onOpenDetailRef.current = onOpenDetail;

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const cal = new Calendar(containerRef.current, {
      plugins: [interaction, dayGridPlugin],
      initialView: 'dayGridMonth',
      height: 'auto',
      locale: esLocale,
      firstDay: 1,
      buttonText: { today: 'Hoy' },
      headerToolbar: { left: 'title', right: 'today prev,next' },
      showNonCurrentDates: false,
      dayMaxEvents: 3,
      eventDisplay: 'block',
      eventClick: (arg) => onOpenDetailRef.current(arg.event.id),
      eventContent: (arg) => {
        const { proper, sdr } = arg.event.extendedProps;
        const wrap = document.createElement('div');
        wrap.className = 'dv2-ev';
        const title = document.createElement('div');
        title.className = 'dv2-ev__title';
        title.textContent = arg.event.title;
        wrap.appendChild(title);
        const people: [string | undefined, string][] = [
          [proper, 'Proper'],
          [sdr, 'SDR'],
        ];
        const shown = people.filter(([name]) => !isMissing(name));
        if (shown.length) {
          const badges = document.createElement('div');
          badges.className = 'dv2-ev__badges';
          shown.forEach(([name, role]) => {
            const b = document.createElement('span');
            b.className = 'dv2-ev__badge';
            const dot = document.createElement('i');
            dot.className = 'dv2-ev__dot';
            dot.style.background = colorFor(name as string);
            // Nombre corto para que ambas etiquetas quepan en una sola línea.
            b.title = `${role}: ${name}`;
            b.appendChild(dot);
            b.appendChild(
              document.createTextNode(
                `${role === 'SDR' ? 'SDR ' : ''}${(name as string).trim().split(/\s+/)[0]}`,
              ),
            );
            badges.appendChild(b);
          });
          wrap.appendChild(badges);
        }
        return { domNodes: [wrap] };
      },
    });
    cal.render();
    calendarRef.current = cal;
    return () => {
      cal.destroy();
      calendarRef.current = null;
    };
  }, []);

  // Los eventos se vuelven a armar en cada render del dashboard: sólo se
  // repintan cuando cambia su contenido.
  const eventsKey = events
    .map(
      (e) =>
        `${e.id}|${e.start}|${e.color}|${e.proper}|${e.sdr}|${e.id === selectedVisitId}`,
    )
    .join(',');

  useEffect(() => {
    const cal = calendarRef.current;
    if (!cal) return;
    cal.removeAllEvents();
    events.forEach((event) =>
      cal.addEvent({
        id: event.id,
        title: event.title,
        start: event.start,
        backgroundColor: event.color,
        borderColor: event.color,
        classNames: event.id === selectedVisitId ? ['is-selected'] : [],
        extendedProps: { proper: event.proper, sdr: event.sdr },
      }),
    );
    // Lleva el calendario al mes de la visita seleccionada.
    const selected = events.find((event) => event.id === selectedVisitId);
    if (selected) cal.gotoDate(selected.start);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsKey]);

  return (
    <Card className="dv2-calendar">
      <CardBody>
        <div ref={containerRef} />
      </CardBody>
      {footer && <CardFooter>{footer}</CardFooter>}
    </Card>
  );
};

export default DashboardVisitsCalendar;
